// EXTERNAL DEPENDENCIES
import React, { useEffect, useState } from 'react'

// COMPONENTS
import { Button, Row } from '@paypalcorp/pp-react'

// HELPERS
import { usePrefixedContent } from '@/utils'

// TEST IDS
import {
  NEXT_AND_BACK_BUTTONS_CONTAINER_TEST_ID,
  NEXT_BUTTON_TEST_ID,
} from '@/constants/test-ids'

// STYLES
import styles from './styles.module.less'

// TYPES
import { NavigationButtonProps } from '@/types/components'

/**
 * Common next and back button to be displayed at the
 * end of the form with an optional link
 */
const NextAndBackButtons: React.FC<{
  nextButton: NavigationButtonProps
  backButton?: NavigationButtonProps
  pageIsValidated?: boolean
  isLoading?: boolean
  optionalLink?: React.ReactNode
}> = ({
  backButton = {} as NavigationButtonProps,
  nextButton = {} as NavigationButtonProps,
  optionalLink,
  pageIsValidated = true,
  isLoading,
}) => {
  const nextPanelButtonId = 'nextPanelButton'
  const [nextButtonClicked, setNextButtonClicked] = useState(false)
  const [buttonState, setButtonState] = useState<'initial' | 'processing'>(
    'initial',
  )

  // INITIALIZE CONTENT
  const getContent = usePrefixedContent('components.ui.next-button.')
  const defaultNextButtonText = getContent('nextButton')
  const defaultBackButtonText = getContent('backButton')

  function onNextClick(e: React.FormEvent<HTMLFormElement>) {
    setNextButtonClicked(true)
    nextButton.handleClick(e)
  }

  useEffect(() => {
    if (nextButtonClicked && pageIsValidated && buttonState === 'processing') {
      setNextButtonClicked(false)

      setTimeout(() => {
        document.getElementById(nextPanelButtonId).click()
      }, 100)
    }
  }, [nextButtonClicked, pageIsValidated, buttonState])

  useEffect(() => {
    if (nextButtonClicked) {
      setButtonState(pageIsValidated ? 'initial' : 'processing')
    }
  }, [nextButtonClicked, pageIsValidated])

  return (
    <Row
      noGutters
      className={styles.nextButtonComponentContainer}
      data-testid={NEXT_AND_BACK_BUTTONS_CONTAINER_TEST_ID}
    >
      {isLoading ? null : (
        <>
          <Row noGutters className={styles.buttonsContainer}>
            {backButton.handleClick ? (
              <Button
                secondary
                fullWidthOnMobile
                onClick={backButton.handleClick}
                type={backButton.type || 'button'}
                data-fpti={backButton.fpti}
              >
                {backButton.text || defaultBackButtonText}
              </Button>
            ) : null}
            <Button
              fullWidthOnMobile
              id={nextPanelButtonId}
              className={styles.nextButton}
              type={nextButton.type || 'button'}
              btnState={buttonState}
              data-fpti={nextButton.fpti}
              data-testid={NEXT_BUTTON_TEST_ID}
              onClick={onNextClick}
            >
              {nextButton.text || defaultNextButtonText}
            </Button>
          </Row>
          {optionalLink || null}
        </>
      )}
    </Row>
  )
}

export default NextAndBackButtons
